import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { ArrowRight, Phone, MapPin, Mail, Instagram, Facebook, Youtube } from "lucide-react";
import logo from "../../assets/logo.png";
import { SITE_NAV } from "./Navbar";
import { SponsorStrip } from "@/components/Sponsors";
import { useFooter } from "@/lib/useContent";

export const FinalCTA = () => {
  const footer = useFooter();
  return (
  <section className="relative py-20 sm:py-28 bg-[#eef1f7]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-navy px-6 py-14 sm:px-16 sm:py-20 text-center">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-gold/20 blur-3xl" />
          <div className="relative">
            <span className="section-tag section-tag--dark">Sé parte de los próximos 50 años</span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight max-w-3xl mx-auto leading-tight" style={{ fontFamily: "Outfit" }}>
              Tu club te espera. Hacete socio del Comandante Leo Nowak
            </h2>
            <p className="mt-5 text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              Sumate a una comunidad de más de 800 familias. Fútbol, quincho, eventos y una historia de medio siglo que se sigue escribiendo.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                data-testid="final-cta-primary"
                to="/membresias"
                className="group inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold text-base px-8 py-4 rounded-xl shadow-xl hover:-translate-y-0.5 transition-transform"
              >
                Asociarme ahora <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${footer.phone}`}
                data-testid="final-cta-call"
                className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/30 text-white font-semibold text-base px-8 py-4 rounded-xl hover:bg-white/20 transition-colors"
              >
                <Phone className="h-5 w-5" /> Llamar al club
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

export const Footer = () => {
  const footer = useFooter();
  const socials = [
    { Icon: Instagram, url: footer.instagram },
    { Icon: Facebook, url: footer.facebook },
    { Icon: Youtube, url: footer.youtube },
  ];
  return (
  <footer className="bg-navy text-white">
    <SponsorStrip />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Club Comandante Leo Nowak" className="h-14 w-auto" />
            <div className="leading-tight">
              <div className="font-extrabold text-white" style={{ fontFamily: "Outfit" }}>COMANDANTE LEO NOWAK</div>
              <div className="text-xs font-semibold tracking-[0.18em] text-gold">CLUB · 1976—2026</div>
            </div>
          </Link>
          <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-xs">
            {footer.description}
          </p>
          <div className="mt-6 flex gap-3">
            {socials.map(({ Icon, url }, i) => (
              <a key={i} href={url || "#"} target={url ? "_blank" : undefined} rel={url ? "noreferrer" : undefined} data-testid={`social-link-${i}`} aria-label="Red social del club"
                className="h-10 w-10 rounded-lg bg-white/10 hover:bg-gold hover:text-navy flex items-center justify-center transition-colors">
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-sm font-bold text-gold uppercase tracking-wider">Navegación</h3>
          <ul className="mt-4 space-y-2.5">
            {SITE_NAV.map((l) => (
              <li key={l.to}>
                <Link data-testid={`footer-link-${l.to.slice(1)}`} to={l.to} className="text-white/70 hover:text-white text-sm transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link data-testid="footer-link-membresias" to="/membresias" className="text-white/70 hover:text-white text-sm transition-colors">Socios</Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-5">
          <h3 className="text-sm font-bold text-gold uppercase tracking-wider">Contacto</h3>
          <ul className="mt-4 space-y-3.5 text-sm text-white/70">
            {footer.address && <li className="flex items-start gap-3" data-testid="footer-address"><MapPin className="h-5 w-5 text-gold shrink-0" /> {footer.address}</li>}
            {footer.phone_display && <li className="flex items-center gap-3" data-testid="footer-phone"><Phone className="h-5 w-5 text-gold shrink-0" /> {footer.phone_display}</li>}
            {footer.email && <li className="flex items-center gap-3" data-testid="footer-email"><Mail className="h-5 w-5 text-gold shrink-0" /> {footer.email}</li>}
          </ul>
          {footer.hours && (
            <div className="mt-5 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm">
              <span className="text-white/70">Horario sede:</span>
              <span className="font-semibold text-white" data-testid="footer-hours">{footer.hours}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-12 pt-7 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-white/60">© {new Date().getFullYear()} Club Comandante Leo Nowak. Todos los derechos reservados.</p>
        <p className="text-xs text-white/50">Bodas de Oro · 1976 — 2026</p>
      </div>
    </div>
  </footer>
  );
};
